// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { Action } from "redux"
import { CreateTagKeywordRequestSchema } from "generated/legebill/site/tag/v4/tag_pb"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { TagKeyword, TagKeywordSchema } from "generated/legebill/type/tag_pb"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"
import { Code, executorMutation, Init, isConnectError, tagService } from "../../../app/rpc"
import { create } from "@bufbuild/protobuf"

type CreateTagKeyword = (request: Init<typeof CreateTagKeywordRequestSchema>) => Observable<TagKeyword>

export const createTagKeyword = (createTagKeywordRpc: CreateTagKeyword) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.createTagKeyword.match),
      mergeMap(({ payload }) => createTagKeywordRpc(payload).pipe(
        map(Slice.createTagKeywordFulfilled),
        catchError(e => handleError(
          e,
          payload.keyword ? create(TagKeywordSchema, payload.keyword) : undefined
        )),
      ))
    )

const handleError = (e: unknown, tagKeyword: TagKeyword | undefined): Observable<Action> => {
  if (isConnectError(e) && tagKeyword) {
    const { code } = e
    if (code === Code.AlreadyExists) {
      // Already exists, fetch the tag again as the frontend might be out-of-sync
      return of(Actions.getTag({
        request: { tagId: tagKeyword.tagId }
      }))
    }
    if (code === Code.NotFound) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_get_tag_not_found", message: "Tag not found. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
          messageType: "ERROR",
        })),
        of(Slice.setDialogStatus("CLOSED")),
        of(Slice.removeFromCache(tagKeyword.tagId))
      )
    }
    if (code === Code.PermissionDenied) {
      return of(showSnackBar({
        message: t({ id: "msg_create_tag_keyword_permission_denied", message: "Failed to add keyword. You do not have permission to edit tags." }),
        messageType: "ERROR"
      }))
    }
  }

  return of(showSnackBar({
    message: t({ id: "msg_create_tag_keyword_failed", message: "Failed to add keyword. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

const createTagKeywordRpc = (request: Init<typeof CreateTagKeywordRequestSchema>): Observable<TagKeyword> =>
  executorMutation(() => tagService.createTagKeyword(create(CreateTagKeywordRequestSchema, request)))

export default createTagKeyword(createTagKeywordRpc)
